import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CalendarIcon, UserIcon, TagIcon, LinkedinIcon } from 'lucide-react';
import Button from '../../components/Button';
import { Helmet } from 'react-helmet';
const CatalogDataQuality = () => {
  return <div className="bg-white min-h-screen" data-id="element-199">
      <Helmet data-id="element-200">
        <title data-id="element-201">
          It's not me, it's you: The Real Problem in AI Commerce | Shopilots
          Blog
        </title>
        <meta name="description" content="In AI commerce, the real problem is rarely the agent — it's the catalog feed. Learn how catalog data quality impacts AI performance and what retailers need to do before Amazon's November 2025 deadline." data-id="element-202" />
        <meta name="keywords" content="AI commerce, catalog data, Amazon ASIN, product data, AI retail, catalog management" data-id="element-203" />
      </Helmet>
      {/* Hero Section with Image */}
      <div className="relative h-96" data-id="element-204">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/f44aHP4XWcsXXBqXG8GYEL/blog_image.jpg" alt="Catalog data quality diagram showing the flow from catalog to AI agents" className="w-full h-full object-cover" data-id="element-205" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end" data-id="element-206">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full" data-id="element-207">
            <Link to="/blog" data-id="element-208">
              <Button variant="outline" className="mb-4 bg-white/20 backdrop-blur-sm text-white border-white hover:bg-white/30" data-id="element-209">
                <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-210" />
                Back to Blog
              </Button>
            </Link>
            <h1 className="text-4xl font-bold text-white mb-4" data-id="element-211">
              "It's not me, it's you." The Real Problem in AI Commerce
            </h1>
            <div className="flex items-center text-white/80 text-sm" data-id="element-212">
              <CalendarIcon className="h-4 w-4 mr-1" data-id="element-213" />
              <span data-id="element-214">September 10, 2023</span>
              <span className="mx-2" data-id="element-215">•</span>
              <UserIcon className="h-4 w-4 mr-1" data-id="element-216" />
              <span data-id="element-217">Ben Geva</span>
              <span className="mx-2" data-id="element-218">•</span>
              <TagIcon className="h-4 w-4 mr-1" data-id="element-219" />
              <span data-id="element-220">AI Retail</span>
            </div>
          </div>
        </div>
      </div>
      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-221">
        <div className="flex items-center mb-8" data-id="element-222">
          <img src="https://media.licdn.com/dms/image/D4D03AQEiIyXDM2QcWg/profile-displayphoto-shrink_200_200/0/1695651798652?e=1699488000&v=beta&t=8ZYfVcnE2Dz0w5aABzA-jGF5vXIiRZ-mMx1auNZWTRY" alt="Ben Geva" className="w-16 h-16 rounded-full mr-4" data-id="element-223" />
          <div data-id="element-224">
            <h3 className="font-bold text-lg" data-id="element-225">Ben Geva</h3>
            <p className="text-gray-600" data-id="element-226">Founder & CEO, Shopilots</p>
            <a href="https://www.linkedin.com/in/bengeva/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 inline-flex items-center text-sm mt-1" data-id="element-227">
              <LinkedinIcon className="h-4 w-4 mr-1" data-id="element-228" />
              Connect on LinkedIn
            </a>
          </div>
        </div>
        <div className="prose prose-lg max-w-none prose-indigo mb-12" data-id="element-229">
          <p data-id="element-230">
            In AI commerce, the real problem is rarely the agent — it's the
            catalog feed.
          </p>
          <p data-id="element-231">
            Amazon just rang the alarm bell: over 74,000 outdated variation
            themes are being deprecated. Every retailer must update their ASINs
            by November 2025 or risk losing visibility, reviews aggregation, and
            conversion power.
          </p>
          <p data-id="element-232">
            <a href="https://sellercentral.amazon.com/help/hub/reference/GXDLDC7VCMMKTKGK" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800" data-id="element-233">
              👉 Amazon documentation on ASIN variation changes
            </a>
          </p>
          <p data-id="element-234">
            This is not just housekeeping — retailers have till November to make
            the changes. It's a clear signal for the entire retail industry:
          </p>
          <ol data-id="element-235">
            <li data-id="element-236">
              <strong data-id="element-237">Align your catalog with the new Amazon guidelines</strong>{' '}
              — because Rufus and other GenAI engines cannot optimize on messy
              data.
            </li>
            <li data-id="element-238">
              <strong data-id="element-239">Extend your digital offering</strong> — your product data,
              website, and customer touchpoints must meet the expectations of
              the AI era.
            </li>
          </ol>
          <p data-id="element-240">
            Because ShoPilots is an AI-first platform, we built this anchoring
            into our onboarding logic from day one: flattening, normalizing, and
            standardizing messy catalog data so AI can actually sell.
          </p>
          <p data-id="element-241">
            We create the hidden layer that enables AI agents to sell with your
            authentic voice.
          </p>
          <p data-id="element-242">
            That's why our platform moves clients from feature-based shopping
            ("65-inch OLED panel") to need-led shopping ("a TV that works in a
            bright living room, perfect for movies").
          </p>
          <figure data-id="element-243">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/f44aHP4XWcsXXBqXG8GYEL/blog_image.jpg" alt="Diagram showing the transition from feature-based to need-led shopping" className="w-full rounded-lg" data-id="element-244" />
            <figcaption className="text-center text-gray-600 text-sm mt-2" data-id="element-245">
              The transformation from feature-based to need-led shopping
              requires clean, structured catalog data
            </figcaption>
          </figure>
          <p data-id="element-246">
            This shift isn't cosmetic — it drives better exposure in GenAI
            engines and website uplift in conversion rates. Those who adapt now
            will win in search, exposure, and conversion. Those who wait will
            fall behind.
          </p>
          <p data-id="element-247">
            Retailers don't need "another chatbot." They need a robust platform
            that:
          </p>
          <ol type="A" data-id="element-248">
            <li data-id="element-249">Cleans and anchors the catalog</li>
            <li data-id="element-250">
              Connects it to AI agents across channels (website, social, GPT,
              messaging)
            </li>
            <li data-id="element-251">Continuously learns from outcomes</li>
          </ol>
          <p data-id="element-252">
            That's what we've built for our partner retailers — already taking
            the lead in the AI era.
          </p>
        </div>
        {/* CTA Section */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-white shadow-xl" data-id="element-253">
          <h2 className="text-2xl font-bold mb-4" data-id="element-254">
            Ready to prepare your catalog for the AI era?
          </h2>
          <p className="mb-6" data-id="element-255">
            Let our experts help you transform your product data into a powerful
            asset for AI-driven commerce.
          </p>
          <div className="flex flex-wrap gap-4" data-id="element-256">
            <a href="https://www.linkedin.com/in/bengeva/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-indigo-700 bg-white hover:bg-indigo-50" data-id="element-257">
              <LinkedinIcon className="mr-2 h-5 w-5" data-id="element-258" />
              Connect with Ben
            </a>
            <a href="mailto:ben@shopilots.com?subject=Catalog%20Data%20Transformation" className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-indigo-700" data-id="element-259">
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </div>;
};
export default CatalogDataQuality;